import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createParagraphNode, $getNearestNodeFromDOMNode, $getRoot } from "lexical";
import { useEffect } from "react";

import type { FC } from "react";

export const ClickToAppendPlugin: FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerRootListener((rootElement) => {
        if (rootElement !== null) {
          const onMouseUp = (event: MouseEvent): void => {
            // 1. Get the browser selection
            const selection = globalThis.getSelection();
            const lastElement = rootElement.lastElementChild;
            const clickedElement = Array.from(rootElement.children).find((element) => {
              const { top, bottom } = element.getBoundingClientRect();
              return event.clientY >= top && event.clientY <= bottom;
            });
            const eventTarget = event.target instanceof Element ? event.target : null;
            const clickedAtTargetStart =
              eventTarget !== null &&
              eventTarget !== rootElement &&
              event.clientX <= eventTarget.getBoundingClientRect().left + 2;

            // 2. Only proceed if the click was on the root or at the start of a line AND the user
            // isn't highlighting text (selection.isCollapsed is true when no text is highlighted).
            if ((event.target === rootElement || clickedAtTargetStart) && selection?.isCollapsed) {
              editor.update(() => {
                if (clickedElement !== undefined) {
                  const { left } = clickedElement.getBoundingClientRect();
                  if (event.target === rootElement && event.clientX > left) return;

                  const clickedNode = $getNearestNodeFromDOMNode(clickedElement);
                  if (clickedNode === null) return;

                  clickedNode.selectStart();
                  return;
                }

                if (
                  event.target !== rootElement ||
                  (lastElement !== null &&
                    event.clientY <= lastElement.getBoundingClientRect().bottom)
                ) {
                  return;
                }

                const root = $getRoot();
                const lastChild = root.getLastChild();

                if (
                  lastChild === null ||
                  lastChild.getTextContentSize() > 0 ||
                  lastChild.getType() !== "paragraph"
                ) {
                  const p = $createParagraphNode();
                  root.append(p);
                  p.select();
                } else {
                  lastChild.selectEnd();
                }
              });
            }
          };

          // Use mouseup instead of click to better handle the end of a drag
          rootElement.addEventListener("mouseup", onMouseUp);
          return () => rootElement.removeEventListener("mouseup", onMouseUp);
        }
      }),
    [editor],
  );

  return null;
};
